<template>
  <div class="container d-flex flex-column px-4 mt-4">
    <Alert
      v-if="showAlert"
      :message="alertMessage"
      alertIcon="fa fa-info-circle"
      alertType="alert-warning"
      :closable="false"
    />
    <div class="container-fluid" v-if="currentStep === Steps.CHOOSE_AMOUNT">
      <button
        class="mt-3"
        v-if="
          available >= minimumWithdrawalAmount &&
          available >= lastWithdrawal.amount
        "
        :class="selectedAmount === 5 ? 'active-option' : 'option-btn'"
        @click="setSelectedAmount(5)"
      >
        {{ t("CHOOSELASTAMOUNT") }}
        <br />
        {{ currencyFormat(lastWithdrawal.amount) }}
      </button>
      <h2 class="text-center mt-3">
        {{ t("AVAILABLE") }}:
        <span class="fw-bold">{{ currencyFormat(available) }}</span>
      </h2>
      <h6
        v-if="available >= minimumWithdrawalAmount"
        class="mt-3 text-center"
        style="font-weight: normal"
      >
        {{ t("HOWMUCHWITHDRAW") }}
      </h6>
      <div v-if="available >= minimumWithdrawalAmount">
        <div id="options-section" class="mt-10 text-center">
          <button
            class="mt-3"
            :class="selectedAmount === 1 ? 'active-option' : 'option-btn'"
            @click="setSelectedAmount(1)"
          >
            {{ currencyFormat(getWithdrawalAmounts(1)) }}
          </button>
          <button
            class="mt-3"
            v-if="getWithdrawalAmounts(2) > minimumWithdrawalAmount"
            :class="selectedAmount === 2 ? 'active-option' : 'option-btn'"
            @click="setSelectedAmount(2)"
          >
            {{ currencyFormat(getWithdrawalAmounts(2)) }}
          </button>
          <button
            class="mt-3"
            v-if="getWithdrawalAmounts(3) > minimumWithdrawalAmount"
            :class="selectedAmount === 3 ? 'active-option' : 'option-btn'"
            @click="setSelectedAmount(3)"
          >
            {{ currencyFormat(getWithdrawalAmounts(3)) }}
          </button>
          <button
            class="mt-3"
            v-if="available > minimumWithdrawalAmount"
            :class="selectedAmount === 4 ? 'active-option' : 'option-btn'"
            @click="setSelectedAmount(4)"
          >
            {{ t("CUSTOMAMOUNT") }}
          </button>
        </div>
      </div>
      <div v-else class="text-center mt-5">
        <h3>
          {{ t("NOTENOUGHFUNDS") }}
        </h3>
      </div>
    </div>
    <div
      class="floating-btn mt-3"
      v-if="
        available >= minimumWithdrawalAmount &&
        currentStep === Steps.CHOOSE_AMOUNT
      "
    >
      <button
        :class="selectedAmount === 0 && 'disabled-button'"
        class="withdraw-set-btn text-start w-100 p-3"
        @click="
          checkWithdrawalAvailable(
            selectedAmount === 4 ? Steps.CUSTOM_AMOUNT : Steps.CONFIRMATION
          )
        "
      >
        <div class="text-white text-center my-auto fw-bold btn-text">
          <span>{{ t("WITHDRAW") }}</span>
        </div>
      </button>
    </div>
    <div v-if="currentStep === Steps.CUSTOM_AMOUNT">
      <div class="main-body">
        <div class="container">
          <div class="mt-5">
            <h2 class="text-center">
              {{ t("AVAILABLE") }}:
              <span class="fw-bold">{{ currencyFormat(available) }}</span>
            </h2>
            <h6 class="text-center">{{ t("NODECIMALS") }}</h6>
            <input type="number" class="custom-input" v-model="withdrawing" />
          </div>
        </div>
      </div>
      <div class="floating-btn mt-3">
        <button
          :class="withdrawBtnDisabled && 'disabled-button'"
          class="withdraw-set-btn text-start w-100 p-3"
          :disabled="withdrawBtnDisabled"
          @click="checkWithdrawalAvailable(Steps.CONFIRMATION)"
        >
          <div class="text-white text-center my-auto fw-bold btn-text">
            <span>{{ t("WITHDRAW") }}</span>
          </div>
        </button>
      </div>
    </div>
    <IdentityVerification
      v-if="
        currentStep === Steps.IDENTITY_VERIFICATION &&
        requireValidationResponse?.requiredValidations?.length > 0
      "
      @next="handleCurpValidationResult"
      @setScreenTitle="setScreenTitle"
      :require-validation-response="requireValidationResponse"
      :customerName="customerName"
      @on-validation-failed="$router.back()"
      hide-close-button
      :user-flow="validationReference"
    />
    <div
      class="container-fluid"
      id="withdrawal-details"
      v-if="currentStep === Steps.CONFIRMATION"
    >
      <WithdrawalDetails
        :disable-btn="securingWithdrawal"
        :amount="currencyFormat(withdrawing)"
        :totalInterestRate="interestRate"
        :availabilityFee="availabilityFee"
        :insuranceFee="insuranceFee"
        :paymentPlan="`${withdrawalEstimation.paymentTerm} ${t(
          'CL_WITHDRAWAL.PAYMENTS_OF'
        )} ${currencyFormat(withdrawalEstimation.newMinimumPaymentAmount)}`"
        :forwardAccount="
          bankAccountInfo?.clabe && onlineTransfer
            ? FormatBankAccountHelper(bankAccountInfo?.clabe)
            : undefined
        "
        :next-payment="`
        ${currencyFormat(withdrawalEstimation.newMinimumPaymentAmount)} -
          ${new Date(
            withdrawalEstimation.nextPaymentDueDate * 1000
          ).toLocaleDateString()}
        `"
        @next="secureWithdrawal"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, onBeforeMount, onMounted, PropType, Ref, ref, watch } from "vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import Alert from "@/shared-components/common/Alert.vue";
import { Service } from "@/apps/credit-line/services/network.service";
import { AndroidInterface } from "@/apps/credit-line/services/android.service";
import { IWithdrawalEstimate } from "@/apps/credit-line/models/IWithdrawalEstimate";
import currencyFormat from "@/apps/credit-line/helpers/currencyFormatter.helper";
import { useRoute, useRouter } from "vue-router";
import { ErrorHandler } from "@/apps/credit-line/helpers/errorHandlerWithLogger.helper";
import AnalyticEvents from "@/shared-helpers/google-analytics-events.helper";
import logAnalyticsEvent from "@/apps/credit-line/services/google-analytics-events.service";
import { getErrorMessage } from "@/apps/credit-line/helpers/backCodesStrings.helper";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";
import { IWithdrawal, CreditLineDebitType } from "../models/CreditLineDetails";
import WithdrawalDetails from "@/apps/credit-line/components/Withdrawal/WithdrawalDetails.vue";
import { IBankAccountInfo } from "@/apps/credit-line/models/IBankAccountInfo";
import { FormatBankAccountHelper } from "@/apps/credit-line/helpers/formatBankAccount.helper";
import IdentityVerification from "@/apps/credit-line/components/Withdrawal/IdentityVerification.vue";
import { AreValidationsRequiredResponse } from "@/apps/credit-line/models/IKycValidations";
import { useStore } from "vuex";
import { useCreditLineSummary } from "@/apps/credit-line/hooks/useCreditLineSummary";
import { KycUserFlow } from "../enums/kycEnums";
import { GeolocationSuccessResponse } from "../composables/useGeolocationFromNative";
import { calculateEffectiveInterestRate } from "@/shared-helpers/effective-interest-rate.helper";
import { CL_COUNTRY } from "@/apps/credit-line/types/cl_countries";

declare const Android: AndroidInterface;

enum Steps {
  CHOOSE_AMOUNT,
  CUSTOM_AMOUNT,
  IDENTITY_VERIFICATION,
  CONFIRMATION,
}

const props = defineProps({
  onlineTransfer: {
    type: Boolean,
    default: false,
  },
  bankAccountInfo: {
    type: Object as PropType<IBankAccountInfo> | null,
  },
  step: String,
  userId: {
    type: String,
    default: "",
  },
  aml: {
    type: Boolean,
    default: false,
  },
  maxAmount: {
    type: Number,
    default: 0,
  },
  minAmount: {
    type: Number,
    default: 0,
  },
  requireValidationResponse: {
    type: Object as PropType<AreValidationsRequiredResponse>,
    required: true,
  },
  customerName: {
    type: String,
    default: "",
  },
  validationReference: {
    type: String as PropType<KycUserFlow>,
    required: true,
  },
});

const emit = defineEmits([
  "setScreenTitle",
  "next",
  "setPercentage",
  "updateStep",
  "setLoading",
  "showReturn",
  "withdrawData",
]);

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const showAlert = ref(true);
const currentStep = ref<Steps>(Steps.CHOOSE_AMOUNT);
const alertMessage = ref("");
const withdrawing = ref(0);
const selectedAmount = ref(0);
const available = ref(0);
const minimumWithdrawalAmount = ref(0);
const CreditLineService = new Service();
const withdrawalEstimation = ref({} as IWithdrawalEstimate);
const lastWithdrawal = ref({} as IWithdrawal);
const securingWithdrawal = ref(false);
const fetching = ref<boolean>(false);
const validationSuccess = ref<boolean>(false);
const availabilityFee = ref<number | undefined>(undefined);
const insuranceFee = ref<number | undefined>(undefined);
const geolocation = inject<Ref<GeolocationSuccessResponse | null>>("withdrawalGeolocation");

const { summary ,fetchSummary } = useCreditLineSummary();

const country = computed<CL_COUNTRY | null>(
  () => summary.value?.country ?? null
);

const withdrawBtnDisabled = computed(
  () =>
    fetching.value ||
    withdrawing.value === 0 ||
    !Number.isInteger(withdrawing.value)
);

const interestRate = computed(() => {
  const total = currencyFormat(withdrawalEstimation.value.withdrawalTotalPayment);
  const apr = withdrawalEstimation.value.annualPercentageRate;
  const rate = country.value === CL_COUNTRY.CO
    ? calculateEffectiveInterestRate(apr) * 100
    : apr * 100;
  return `${total} - ${rate.toFixed(2)}%`;
});

const setScreenTitle = (title: string) => emit("setScreenTitle", title);

const handleCurpValidationResult = () => {
  validationSuccess.value = true;
  checkWithdrawalAvailable(Steps.CONFIRMATION);
};

onBeforeMount(() => {
  checkWithdrawalInfo();
});

onMounted(() => {
  emit("setScreenTitle", t("CL_WITHDRAWAL.CHOOSE_AMOUNT"));
  available.value = props.maxAmount || Number(route.query.available) || 0;
  minimumWithdrawalAmount.value =
    props.minAmount || Number(route.query.minimumWithdrawal);
  alertMessage.value = props.onlineTransfer
    ? t("CL_WITHDRAWAL.AMOUNT_WARNING")
    : t("MINIMUMWITHDRAW", {
        amount: minimumWithdrawalAmount.value,
      });
  showAlert.value = true;
  if (Android.finishedLoading) {
    Android.finishedLoading();
  }
});

watch(fetching, (val) => emit("setLoading", val));

watch(
  () => props.step,
  (val) => {
    if (val === "chooseAmount") {
      showAlert.value = true;
      alertMessage.value = t("MINIMUMWITHDRAW", {
        amount: minimumWithdrawalAmount.value,
      });
      currentStep.value = Steps.CHOOSE_AMOUNT;
    }
  }
);

const checkWithdrawalInfo = (): void => {
  fetching.value = true;
  fetchSummary()
    .then((summary) => {
      if (summary) {
        store.dispatch("creditLine/setCurrency", summary.currency);
        let withdrawalsLength = summary.withdrawals.length;
        if (withdrawalsLength > 0) {
          let withdrawalsCopy = summary.withdrawals;
          withdrawalsCopy.reverse();
          const match = withdrawalsCopy.find((obj) => {
            return (
              obj.type === CreditLineDebitType.WITHDRAWAL ||
              obj.type === CreditLineDebitType.CASH_TRANSFER
            );
          });
          if (match !== undefined) {
            lastWithdrawal.value = match;
          }
        }
      }
    })
    .catch()
    .finally(() => {
      fetching.value = false;
    });
};

const checkWithdrawalAvailable = (selected: Steps) => {
  if (selected === Steps.CUSTOM_AMOUNT) {
    currentStep.value = selected;
  } else if (
    !props.requireValidationResponse.requiredValidations ||
    validationSuccess.value ||
    props.validationReference != KycUserFlow.creditLineCashWithdrawal
  ) {
    fetching.value = true;
    logAnalyticsEvent(
      AnalyticEvents.CreditLine[`WithdrawOption${selectedAmount.value}`],
      "Withdraw"
    );
    switchStep(selected);
  } else if (
    props.requireValidationResponse.requiredValidations.length > 0 &&
    props.validationReference == KycUserFlow.creditLineCashWithdrawal &&
    !validationSuccess.value
  ) {
    showAlert.value = false;
    currentStep.value = Steps.IDENTITY_VERIFICATION;
  } else if (
    props.requireValidationResponse.requiredValidations.length === 0 &&
    props.validationReference == KycUserFlow.creditLineCashWithdrawal
  ) {
    validationSuccess.value = true;
    fetching.value = true;
    logAnalyticsEvent(
      AnalyticEvents.CreditLine[`WithdrawOption${selectedAmount.value}`],
      "Withdraw"
    );
    switchStep(Steps.CONFIRMATION);
  }
};

const switchStep = (selected: Steps) => {
  if (withdrawing.value >= minimumWithdrawalAmount.value) {
    securingWithdrawal.value = true;
    CreditLineService.getWithdrawalEstimate({
      deviceTag: AuthenticationHelper.getDeviceTag(),
      amount: withdrawing.value,
    })
      .then((response) => {
        fetching.value = false;
        withdrawalEstimation.value = response.data;
        if (
          withdrawalEstimation.value !== undefined &&
          withdrawalEstimation.value.feeSchema?.availabilityFee &&
          withdrawalEstimation.value.feeSchema?.insuranceFee
        ) {
          availabilityFee.value =
            withdrawalEstimation.value.feeSchema?.availabilityFee.rateIncludingTaxPercentage;
          insuranceFee.value =
            withdrawalEstimation.value.feeSchema?.insuranceFee.rateIncludingCommissionPercentage;
        }
        currentStep.value = selected;
        emit("setScreenTitle", t("WITHDRAWALCONFIRMATION"));
        emit("updateStep", "details");
        emit("setPercentage", 100);

        //Change to true to turn on service issues message
        alertMessage.value = t("DELAYED_SERVICE");
        showAlert.value = false;

        securingWithdrawal.value = false;
      })
      .catch((error) => {
        if (error?.response.data?.errorCode) {
          ErrorHandler.logCreditlineErrorResponse(error.response.data);
          alertMessage.value = getErrorMessage(
            error.response.data.errorCode,
            t,
            AuthenticationHelper.getDeviceTag(),
            minimumWithdrawalAmount.value.toString()
          );
        } else {
          ErrorHandler.handleNetworkErrorAndLog(error, () => null);
          alertMessage.value = getErrorMessage(705, t);
        }
        fetching.value = false;
      });
  } else {
    alertMessage.value = t("MINIMUMWITHDRAW", {
      amount: minimumWithdrawalAmount.value,
    });
    showAlert.value = true;
    fetching.value = false;
  }
};

const secureWithdrawal = () => {
  securingWithdrawal.value = true;
  let params = {
    deviceTag: AuthenticationHelper.getDeviceTag(),
    amount: withdrawing.value,
    annualPercentageRate: withdrawalEstimation.value.annualPercentageRate,
    expiry: withdrawalEstimation.value.expiry,
    hashValue: withdrawalEstimation.value.hashValue,
    geolocation: geolocation?.value,
    bankTransfer: !!(props.bankAccountInfo && props.onlineTransfer),
  };
  if (props.aml) {
    emit("showReturn", props.aml);
    emit("withdrawData", params);
  } else {
    CreditLineService.secureWithdrawal(params)
      .then((response) => {
        if (response.data.debitId) {
          showAlert.value = false;
          router.push({
            name: "CreditLineWithdrawalRequest",
          });
        }
      })
      .catch((error) => {
        showAlert.value = true;
        alertMessage.value = getErrorMessage(
          error.response.data.errorCode || 705,
          t
        );
        ErrorHandler.logCreditlineErrorResponse(error.response.data);
        securingWithdrawal.value = false;
      });
  }
};

const setSelectedAmount = (selected: number) => {
  if (selected === 5) {
    withdrawing.value = lastWithdrawal.value.amount;
  } else {
    withdrawing.value = getWithdrawalAmounts(selected);
  }
  selectedAmount.value = selected;
};

const getWithdrawalAmounts = (option: number) => {
  switch (option) {
    case 1: {
      let op1 = available.value / 10;
      if (op1 < minimumWithdrawalAmount.value) {
        return minimumWithdrawalAmount.value;
      } else {
        let op11 = Math.round(op1 * 1e2) / 1e2;
        return Math.ceil(op11 / 10) * 10;
      }
    }
    case 2: {
      let op3 = available.value / 2;
      if (op3 < minimumWithdrawalAmount.value) {
        return minimumWithdrawalAmount.value;
      } else {
        let op33 = Math.round(op3 * 1e2) / 1e2;
        return Math.ceil(op33 / 10) * 10;
      }
    }
    case 3: {
      return Math.floor(available.value);
    }
    default:
      return 0;
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";

.disabled-button {
  opacity: 0.5;
}

.option-btn {
  width: 100%;
  background-color: $pj-color-white;
  border-radius: 5px;
  color: $pj-color-green;
  text-align: center;
  padding-left: 2.5%;
  border-color: $pj-color-green;
  border-width: thin;
  min-height: 50px;
}

.active-option {
  width: 100%;
  border: none;
  background-color: $pj-color-lighter-green;
  border-radius: 5px;
  color: $pj-color-green;
  text-align: center;
  padding-left: 2.5%;
  min-height: 50px;
}

.withdraw-set-btn {
  background: $pj-color-green;
  border-radius: 11px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
  text-align: center;
  letter-spacing: 0.8px;
  color: $pj-color-white;
  height: 58px;
  border: none;
  bottom: 35px;
  left: 18px;
  width: 100%;
}

.custom-input {
  width: 100%;
  text-align: center;
  font-size: calc(1.325rem + 0.9vw);
}

#withdrawal-details {
  width: 100% !important;
}

.floating-btn {
  background-color: white;
  left: 0;
  bottom: 0;
  z-index: 200;
  width: 100%;
  height: 80px;
  font-size: 1rem;
}
</style>
